/**
 * Motion tokens.
 *
 * Motion explains a change of state or of place. Springs are picked by intent.
 * Use `useReducedMotionSafe(name)` from `motion-react` when binding a spring to
 * Framer Motion. It returns `instant` under `prefers-reduced-motion: reduce`.
 *
 * `overlay`, `scrim`, `listItem`, `insertion`, `hydration`, `expand`, and
 * `press` are the tween recipes the charter permits. `useOverlayMotion()`,
 * `useScrimMotion()`, `useListItemMotion()`, `useListInsertion()`, and
 * `useHydrationCrossfade()` in `motion-react` turn them into Framer Motion
 * props.
 */

export interface SpringConfig {
    readonly type: "spring";
    readonly stiffness: number;
    readonly damping: number;
    readonly mass: number;
}

export interface InstantConfig {
    readonly type: "tween";
    readonly duration: number;
}

export type MotionConfig = SpringConfig | InstantConfig;

/** Cubic-bezier control points as Framer Motion accepts them. */
export type BezierCurve = readonly [number, number, number, number];

const breathe: SpringConfig = {
    // Weighty arrival for large surfaces. ~450ms to settle.
    type: "spring",
    stiffness: 60,
    damping: 10,
    mass: 1,
};

const snappy: SpringConfig = {
    // Tactile acknowledgment. ~150ms.
    type: "spring",
    stiffness: 500,
    damping: 35,
    mass: 1,
};

const gentle: SpringConfig = {
    // List item enter/leave. No overshoot.
    type: "spring",
    stiffness: 200,
    damping: 22,
    mass: 1,
};

const morph: SpringConfig = {
    // Layout and size morphs (SlidingPill, layoutId). Critically damped.
    type: "spring",
    stiffness: 350,
    damping: 30,
    mass: 1,
};

const thumb: SpringConfig = {
    // Selection thumbs (SegmentedControl, Switch). Settles in ~200ms with a
    // barely visible overshoot, so the thumb reads as a physical object.
    type: "spring",
    stiffness: 600,
    damping: 40,
    mass: 1,
};

const instant: InstantConfig = {
    // Reduced-motion fallback. A structural disable, not a fast tween.
    type: "tween",
    duration: 0,
};

const standardCurve: BezierCurve = [0.25, 1, 0.5, 1];
const accelerateCurve: BezierCurve = [0.3, 0, 0.8, 0.15];
const decelerateCurve: BezierCurve = [0.05, 0.7, 0.1, 1];
// Press-in: fast start, no overshoot. Press-out: soft spring-like return.
const pressInCurve: BezierCurve = [0, 0, 0.2, 1];
const pressOutCurve: BezierCurve = [0.2, 0.8, 0.2, 1];

const toCss = (curve: BezierCurve): string => `cubic-bezier(${curve.join(", ")})`;

export const motion = {
    springs: {
        breathe,
        snappy,
        gentle,
        morph,
        thumb,
        instant,
    },
    durations: {
        fast: "150ms",
        base: "250ms",
        slow: "400ms",
        breathe: "450ms",
    },
    /**
     * Control state timing. CSS strings, emitted as `--motion-<kebab-key>`.
     * Module stylesheets read these instead of literals.
     */
    control: {
        pressIn: "60ms",
        pressOut: "220ms",
        hover: "120ms",
        focus: "140ms",
        crossfade: "150ms",
        toggle: "200ms",
    },
    /** CSS easing strings for `transition` declarations. Emitted as `--ease-<kebab-key>`. */
    easings: {
        standard: toCss(standardCurve),
        accelerate: toCss(accelerateCurve),
        decelerate: toCss(decelerateCurve),
        emphatic: toCss(pressOutCurve),
        pressIn: toCss(pressInCurve),
        pressOut: toCss(pressOutCurve),
    },
    /** Numeric curves for Framer Motion `ease`. */
    curves: {
        standard: standardCurve,
        accelerate: accelerateCurve,
        decelerate: decelerateCurve,
        pressIn: pressInCurve,
        pressOut: pressOutCurve,
    },
    /**
     * Overlay enter/exit: fade + 4px rise + a small scale from the anchor
     * edge. Seconds, for Framer Motion. `scale` is per overlay class.
     * Elevation shadow lives on the card and travels with this recipe.
     */
    overlay: {
        rise: 4,
        enter: { duration: 0.16, ease: standardCurve },
        exit: { duration: 0.11, ease: accelerateCurve },
        scale: { popover: 0.98, dialog: 0.985 },
    },
    /** Dialog scrim: opacity only. Seconds. */
    scrim: {
        duration: 0.12,
    },
    /**
     * A row that joins a list already on screen: height 0 → auto with a
     * fade, no stagger. Seconds. `useListInsertion()` binds it.
     */
    insertion: {
        duration: 0.22,
        ease: pressOutCurve,
    },
    /**
     * Skeleton → content swap: one opacity crossfade on the swap host, never
     * a per-row entrance. Seconds. `useHydrationCrossfade()` binds it;
     * `ContentSwap` (primitives) applies it.
     */
    hydration: {
        duration: 0.15,
        ease: standardCurve,
    },
    /** List item arrival: fade + 4px rise with a capped stagger. Seconds. */
    listItem: {
        rise: 4,
        duration: 0.16,
        stagger: 0.03,
        maxStaggered: 8,
    },
    /** Expand/collapse: height + fade. Seconds. */
    expand: {
        duration: 0.2,
    },
    /**
     * Press feedback on controls. Seconds. Asymmetric: `duration` is the
     * press-in, `release` the return. Mirrors `control.pressIn/pressOut`.
     */
    press: {
        scale: 0.97,
        duration: 0.06,
        release: 0.22,
    },
    /** Reduced-motion crossfade. Seconds. Mirrors the global stylesheet. */
    reduced: {
        duration: 0.1,
    },
} as const;

export type MotionTokens = typeof motion;
export type SpringName = keyof typeof motion.springs;

// ---------------------------------------------------------------------------
// Scene transition keyframes
//
// Every view transition defines all three phases. Consumers spread the preset
// onto a motion.div that is a direct child of AnimatePresence. Offsets travel
// as a `transform` string (compositor-only policy, see `motion-react`).
// ---------------------------------------------------------------------------

export interface SceneTransitionKeyframes {
    readonly initial: { readonly opacity: number; readonly transform: string };
    readonly animate: { readonly opacity: number; readonly transform: string };
    readonly exit: { readonly opacity: number; readonly transform: string };
}

export const sceneTransitions = {
    keynote: {
        initial: { opacity: 0, transform: "translateY(10px)" },
        animate: { opacity: 1, transform: "translateY(0px)" },
        exit: { opacity: 0, transform: "translateY(-6px)" },
    },
} as const satisfies Record<string, SceneTransitionKeyframes>;
